import React, { createContext, useContext, useState, useEffect } from "react";
import { auth, db, functions, loggedGetDoc } from "../firebase";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  User,
  updatePassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

const ADMIN_FEATURES_ON = import.meta.env.VITE_ADMIN_FEATURES === "on";

// Enhanced RBAC Types
export interface Permission {
  module: string; // 'expenses', 'production', 'stock', 'reports', 'admin'
  action: string; // 'create', 'read', 'update', 'delete', 'approve', 'audit'
  nature?: string; // expense nature for expense module
  link?: string; // specific link/module for granular control
}

export interface RolePermissions {
  role: string;
  permissions: Permission[];
  timeWindows?: {
    startTime: string; // HH:mm format
    endTime: string; // HH:mm format
    daysOfWeek: number[]; // 0-6, Sunday=0
  };
  restrictions?: {
    maxAmount?: number;
    requiresApproval?: boolean;
    approvalThreshold?: number;
  };
}

export interface UserPermissions {
  userId: string;
  roles: string[];
  customPermissions?: Permission[];
  assignedUnits: string[];
  assignedModules: string[];
  assignedNatures: string[];
  timeRestrictions?: {
    timezone: string;
    businessHoursOnly: boolean;
  };
}

// Default role permissions configuration
// STREAMLINED: 6 core roles that cover all business needs
const DEFAULT_ROLE_PERMISSIONS: RolePermissions[] = [
  {
    role: "superadmin",
    permissions: [
      { module: "*", action: "*" }, // All permissions
    ],
  },
  {
    role: "admin",
    permissions: [
      { module: "expenses", action: "*" },
      { module: "production", action: "*" },
      { module: "stock", action: "*" },
      { module: "reports", action: "*" },
      { module: "admin", action: "read" },
      { module: "admin", action: "update", link: "users" },
    ],
  },
  {
    role: "manager",
    permissions: [
      { module: "expenses", action: "read" },
      { module: "expenses", action: "create" },
      { module: "expenses", action: "update" },
      { module: "expenses", action: "approve" }, // Can approve team expenses
      { module: "production", action: "read" },
      { module: "production", action: "create" },
      { module: "production", action: "update" },
      { module: "production", action: "approve" }, // Can approve production
      { module: "stock", action: "read" },
      { module: "stock", action: "create" },
      { module: "stock", action: "update" },
      { module: "reports", action: "read" },
      { module: "reports", action: "create" },
      { module: "reports", action: "update" },
    ],
    restrictions: {
      maxAmount: 100000,
      requiresApproval: true,
      approvalThreshold: 50000,
    },
  },
  {
    role: "auditor",
    permissions: [
      { module: "expenses", action: "read" },
      { module: "expenses", action: "audit" },
      { module: "production", action: "read" },
      { module: "production", action: "audit" },
      { module: "stock", action: "read" },
      { module: "stock", action: "audit" },
      { module: "reports", action: "read" },
      { module: "admin", action: "read", link: "audit-logs" },
    ],
  },
  {
    role: "editor",
    permissions: [
      { module: "expenses", action: "create" },
      { module: "expenses", action: "read" },
      { module: "expenses", action: "update" },
      { module: "stock", action: "create" },
      { module: "stock", action: "read" },
      { module: "stock", action: "update" },
      { module: "production", action: "create" },
      { module: "production", action: "read" },
      { module: "production", action: "update" },
      { module: "reports", action: "read" },
    ],
    restrictions: {
      maxAmount: 50000,
      requiresApproval: true,
      approvalThreshold: 10000,
    },
  },
  {
    role: "user",
    permissions: [
      { module: "expenses", action: "create" },
      { module: "expenses", action: "read" },
      { module: "expenses", action: "update", link: "own" },
      { module: "production", action: "read" },
      { module: "reports", action: "read", link: "own" },
    ],
    restrictions: {
      maxAmount: 10000,
      requiresApproval: true,
    },
  },
];

// Role priority hierarchy (highest to lowest)
// STREAMLINED: Clear hierarchy with no overlaps
const ROLE_PRIORITY = [
  "superadmin",  // 1. System administrator
  "admin",       // 2. Business administrator
  "manager",     // 3. Team lead with approvals (merged: manager + approver + analyst)
  "auditor",     // 4. Compliance & audit specialist
  "editor",      // 5. Data entry & updates
  "user"         // 6. Basic user
];

// Get primary role based on priority hierarchy
const getPrimaryRole = (roles: string[]): string | null => {
  if (!roles || roles.length === 0) return null;

  // Find the highest priority role
  for (const priorityRole of ROLE_PRIORITY) {
    if (roles.includes(priorityRole)) {
      return priorityRole;
    }
  }

  // If no known roles found, return the first role
  return roles[0];
};

interface AppUser extends Omit<User, 'displayName'> {
  displayName?: string | null;
  isProfileIncomplete?: boolean;
}

interface AuthContextType {
  user: AppUser | null;
  roles: string[];
  role: string | null;
  type: string | null;
  assignedUnits: string[];
  assignedModules: string[];
  assignedNatures: string[];
  permissions: Permission[];
  hasPermission: (module: string, action: string, nature?: string, link?: string) => boolean;
  isWithinTimeWindow: () => boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
  loading: boolean;
  adminCreateUser: (
    email: string,
    password: string,
    roles: string[],
    assignedUnits: string[],
    assignedModules: string[],
    assignedNatures: string[],
    disabled: boolean
  ) => Promise<{ success?: string; error?: string }>;
  adminResetPassword: (email: string, newPassword: string) => Promise<{ success?: string; error?: string }>;
  changeOwnPassword: (newPassword: string) => Promise<{ success?: string; error?: string }>;
  sendResetEmail: (email: string) => Promise<{ success?: string; error?: string }>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  roles: [],
  role: null,
  type: null,
  assignedUnits: [],
  assignedModules: [],
  assignedNatures: [],
  permissions: [],
  hasPermission: () => false,
  isWithinTimeWindow: () => true,
  login: async () => {},
  logout: async () => {},
  error: null,
  loading: true,
  adminCreateUser: async () => ({ error: "Not implemented" }),
  adminResetPassword: async () => ({ error: "Not implemented" }),
  changeOwnPassword: async () => ({ error: "Not implemented" }),
  sendResetEmail: async () => ({ error: "Not implemented" }),
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [role, setRole] = useState<string | null>(null);
  const [type, setType] = useState<string | null>(null);
  const [assignedUnits, setAssignedUnits] = useState<string[]>([]);
  const [assignedModules, setAssignedModules] = useState<string[]>([]);
  const [assignedNatures, setAssignedNatures] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Calculate permissions based on roles
  const calculatePermissions = (userRoles: string[], userModules: string[], userNatures: string[]): Permission[] => {
    const allPermissions: Permission[] = [];

    userRoles.forEach(roleName => {
      const roleConfig = DEFAULT_ROLE_PERMISSIONS.find(r => r.role === roleName);
      if (roleConfig) {
        roleConfig.permissions.forEach(perm => {
          // Filter permissions based on assigned modules and natures
          if (perm.module === "*" ||
              (userModules.includes(perm.module) || userModules.length === 0)) {
            if (!perm.nature || userNatures.includes(perm.nature) || userNatures.length === 0) {
              allPermissions.push(perm);
            }
          }
        });
      }
    });

    return allPermissions;
  };

  // Check if user has specific permission
  const hasPermission = (module: string, action: string, nature?: string, link?: string): boolean => {
    return permissions.some(perm => {
      const moduleMatch = perm.module === "*" || perm.module === module;
      const actionMatch = perm.action === "*" || perm.action === action;
      const natureMatch = !perm.nature || !nature || perm.nature === nature;
      const linkMatch = !perm.link || !link || perm.link === link;

      return moduleMatch && actionMatch && natureMatch && linkMatch;
    });
  };

  // Check if current time is within allowed time window
  const isWithinTimeWindow = (): boolean => {
    if (!role) return true;

    const roleConfig = DEFAULT_ROLE_PERMISSIONS.find(r => r.role === role);
    if (!roleConfig?.timeWindows) return true;

    const now = new Date();
    const currentTime = now.toTimeString().slice(0, 5); // HH:mm format
    const currentDay = now.getDay();

    const { startTime, endTime, daysOfWeek } = roleConfig.timeWindows;

    // Check if current day is allowed
    if (!daysOfWeek.includes(currentDay)) return false;

    // Check if current time is within window
    return currentTime >= startTime && currentTime <= endTime;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      setError(null);
      setRoles([]);
      setRole(null);
      setType(null);
      setAssignedUnits([]);
      setAssignedModules([]);
      setAssignedNatures([]);
      setPermissions([]);

      if (!u) {
        setUser(null);
        setLoading(false);
        return;
      }

      // set basic auth user immediately
      setUser(u as AppUser);

      try {
        const userRef = doc(db, "users", u.uid);
        const snap = await loggedGetDoc(userRef);        if (!snap.exists()) {
          // Automatically create user document with basic permissions
          try {
            const defaultUserDoc = {
              email: u.email || "",
              roles: ["user"],
              assignedUnits: [],
              assignedModules: [],
              assignedNatures: [],
              displayName: u.displayName || "",
              disabled: false,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            };
            
            await setDoc(userRef, defaultUserDoc);
            
            // Set the user with the newly created data
            const enrichedUser: AppUser = {
              ...u,
              displayName: defaultUserDoc.displayName,
              isProfileIncomplete: !defaultUserDoc.displayName,
            };

            setUser(enrichedUser);
            setRoles(defaultUserDoc.roles);
            
            // Set primary role based on priority
            const primaryRole = getPrimaryRole(defaultUserDoc.roles);
            setRole(primaryRole);
            setType(null);
            setAssignedUnits(defaultUserDoc.assignedUnits);
            setAssignedModules(defaultUserDoc.assignedModules || []);
            setAssignedNatures(defaultUserDoc.assignedNatures || []);
            setPermissions(calculatePermissions(defaultUserDoc.roles, defaultUserDoc.assignedModules || [], defaultUserDoc.assignedNatures || []));
          } catch (createError) {
            console.error("Failed to create user document:", createError);
            setError("Failed to create user profile. Contact admin.");
            await signOut(auth);
          }
          setLoading(false);
          return;
        }

        const data = snap.data();
        if (data.disabled) {
          setError("Your account has been disabled. Please contact admin.");
          await signOut(auth);
          setLoading(false);
          return;
        }

        const enrichedUser: AppUser = {
          ...u,
          displayName: data.displayName || u.displayName,
          isProfileIncomplete: !data.displayName,
        };

        setUser(enrichedUser);
        const roleList = Array.isArray(data.roles) ? data.roles : [data.role || "user"];
        if (roleList.includes("disabled") || data.role === "disabled") {
          setError("Your account has been disabled. Please contact admin.");
          await signOut(auth);
          setLoading(false);
          return;
        }
        
        setRoles(roleList);
        
        // Set primary role based on priority (highest first)
        const primaryRole = getPrimaryRole(roleList);
        setRole(primaryRole);
        setType(data.type || null);
        
        const assignedUnitsArray = data.assignedUnits || [];
        setAssignedUnits(assignedUnitsArray);
        setAssignedModules(data.assignedModules || []);
        setAssignedNatures(data.assignedNatures || []);
        setPermissions(calculatePermissions(roleList, data.assignedModules || [], data.assignedNatures || []));
      } catch (err) {
        console.error("AuthContext load error:", err);
        setError("Failed to load user profile.");
        setUser(null);
        setRoles([]);
        setRole(null);
        setType(null);
        setAssignedUnits([]);
        setAssignedModules([]);
        setAssignedNatures([]);
        setPermissions([]);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
    setError(null);
    setLoading(true);
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      const snap = await loggedGetDoc(doc(db, "users", credential.user.uid));      if (!snap.exists()) {
        // Automatically create user document with basic permissions
        try {
          const defaultUserDoc = {
            email: credential.user.email || "",
            roles: ["user"],
            assignedUnits: [],
            assignedModules: [],
            assignedNatures: [],
            displayName: credential.user.displayName || "",
            disabled: false,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };
          
          await setDoc(doc(db, "users", credential.user.uid), defaultUserDoc);
          setLoading(false);
          return; // Let the auth state change handler handle the rest
        } catch (createError) {
          console.error("Failed to create user document during login:", createError);
          setError("Failed to create user profile. Contact admin.");
          await signOut(auth);
          setUser(null);
          setRoles([]);
          setRole(null);
          setType(null);
          setAssignedUnits([]);
          setLoading(false);
          return;
        }
      }
      if (
        snap.exists() &&
        (snap.data()?.disabled || snap.data()?.role === "disabled")
      ) {
        setError("Your account has been disabled. Please contact admin.");
        await signOut(auth);
        setUser(null);
        setRoles([]);
        setRole(null);
        setType(null);
        setAssignedUnits([]);
        setLoading(false);
        return;
      }
    } catch (err: any) {
      setError("Invalid email or password.");
      setLoading(false);
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
    setRoles([]);
    setRole(null);
    setType(null);
    setAssignedUnits([]);
    setAssignedModules([]);
    setAssignedNatures([]);
    setPermissions([]);
    setError(null);
    setLoading(false);
  };

  const adminCreateUser = async (
    email: string,
    password: string,
    roles: string[],
    assignedUnits: string[],
    assignedModules: string[],
    assignedNatures: string[],
    disabled: boolean
  ) => {
    if (!ADMIN_FEATURES_ON) {
      return { error: "Admin user creation requires paid plan. Ask user to sign up." };
    }
    try {
      const fn = httpsCallable(functions, "adminCreateUser");
      const res: any = await fn({
        email,
        password,
        roles,
        assignedUnits,
        assignedModules,
        assignedNatures,
        disabled
      });
      if (res.data?.error) throw new Error(res.data.error);
      return { success: "User created successfully!" };
    } catch (err: any) {
      return { error: err.message || "Failed to create user" };
    }
  };

  const adminResetPassword = async (email: string, newPassword: string) => {
    if (!ADMIN_FEATURES_ON) {
      return { error: "Admin password reset requires paid plan." };
    }
    try {
      const fn = httpsCallable(functions, "adminResetPassword");
      const res: any = await fn({ email, newPassword });
      if (res.data?.error) throw new Error(res.data.error);
      return { success: "Password reset successfully!" };
    } catch (err: any) {
      return { error: err.message || "Failed to reset password" };
    }
  };

  const changeOwnPassword = async (newPassword: string) => {
    try {
      if (!auth.currentUser) throw new Error("Not logged in!");
      await updatePassword(auth.currentUser, newPassword);
      return { success: "Password changed successfully!" };
    } catch (err: any) {
      return { error: err.message || "Failed to change password" };
    }
  };

  const sendResetEmail = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: "Check your email for reset instructions!" };
    } catch (err: any) {
      return { error: err.message || "Failed to send reset email" };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        roles,
        role,
        type,
        assignedUnits,
        assignedModules,
        assignedNatures,
        permissions,
        hasPermission,
        isWithinTimeWindow,
        login,
        logout,
        error,
        loading,
        adminCreateUser,
        adminResetPassword,
        changeOwnPassword,
        sendResetEmail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
